/** 点选改页：多选节点引用、剪贴板序列化、组装 Hermes 提示词 */

import type { PickRefComposerContent } from "./pick-ref-mixed-editor";
import type { PreviewPickItem } from "./brand-site-preview-nav";
import { PICK_EDIT_MARKER_END, PICK_EDIT_MARKER_START } from "./brand-site-preview-nav";

export type PickRef = {
  id: string;
  tag: string;
  text: string;
  path: string;
  html: string;
};

export type PickRefPageContext = {
  pageSlug: string;
  pageTitle: string | null;
  pagePath: string;
};

export type PickRefBundle = PickRefPageContext & {
  picks: PickRef[];
};

export const PICK_BUNDLE_MARKER_START = "<!--c456-pick-bundle:";
export const PICK_BUNDLE_MARKER_END = "-->";

export function pickRefId(path: string): string {
  return path;
}

export function pickRefFromPayload(pick: PreviewPickItem): PickRef {
  return {
    id: pickRefId(pick.path),
    tag: pick.tag,
    text: pick.text,
    path: pick.path,
    html: pick.html,
  };
}

/** Cursor Design 式短代号：@1、@2…（混排文案与提示词共用） */
export function pickRefAlias(index: number): string {
  return `@${index + 1}`;
}

/** 芯片悬停 / 索引行：`<tag> 可见文字` */
export function pickRefChipLabel(ref: PickRef): string {
  const text = ref.text.trim().slice(0, 28);
  if (text) return `<${ref.tag}> ${text}`;
  return `<${ref.tag}>`;
}

/** 芯片可见文案：仅短代号 */
export function pickRefChipDisplay(index: number): string {
  return pickRefAlias(index);
}

export function mergePickRefs(current: PickRef[], incoming: PickRef[]): PickRef[] {
  const map = new Map(current.map((ref) => [ref.id, ref]));
  for (const ref of incoming) map.set(ref.id, ref);
  return [...map.values()];
}

export function removePickRef(current: PickRef[], id: string): PickRef[] {
  return current.filter((ref) => ref.id !== id);
}

function oneLine(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function envelopeField(name: string, value: string): string {
  return `${name}: ${oneLine(value)}`;
}

function encodeBundle(bundle: PickRefBundle): string {
  const json = JSON.stringify(bundle);
  if (typeof btoa === "function") {
    return btoa(unescape(encodeURIComponent(json)));
  }
  return Buffer.from(json, "utf8").toString("base64");
}

function decodeBundle(encoded: string): PickRefBundle | null {
  try {
    const json =
      typeof atob === "function"
        ? decodeURIComponent(escape(atob(encoded)))
        : Buffer.from(encoded, "base64").toString("utf8");
    const parsed = JSON.parse(json) as PickRefBundle;
    if (!parsed || !Array.isArray(parsed.picks) || parsed.picks.length === 0) return null;
    return {
      pageSlug: String(parsed.pageSlug ?? ""),
      pageTitle: parsed.pageTitle ?? null,
      pagePath: String(parsed.pagePath ?? ""),
      picks: parsed.picks.map((pick) => ({
        id: pickRefId(String(pick.path ?? pick.id ?? "")),
        tag: String(pick.tag ?? "div"),
        text: String(pick.text ?? ""),
        path: String(pick.path ?? ""),
        html: String(pick.html ?? ""),
      })),
    };
  } catch {
    return null;
  }
}

/** 复制到剪贴板：含隐藏 bundle + 可见短代号，可粘贴进 Hermes 输入框。 */
export function serializePickRefBundleForClipboard(bundle: PickRefBundle): string {
  const chips = bundle.picks.map((_, index) => pickRefAlias(index)).join(" ");
  return `${PICK_BUNDLE_MARKER_START}${encodeBundle(bundle)}${PICK_BUNDLE_MARKER_END}${chips}`;
}

/** 从粘贴文本解析 bundle；返回去掉 marker 后的余下文本。 */
export function parsePickRefBundleFromText(text: string): {
  bundle: PickRefBundle;
  remainder: string;
} | null {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith(PICK_BUNDLE_MARKER_START)) return null;
  const end = trimmed.indexOf(PICK_BUNDLE_MARKER_END);
  if (end < 0) return null;
  const encoded = trimmed.slice(PICK_BUNDLE_MARKER_START.length, end);
  const bundle = decodeBundle(encoded);
  if (!bundle) return null;
  let remainder = trimmed.slice(end + PICK_BUNDLE_MARKER_END.length).trimStart();
  const chipsOnly = bundle.picks.map((_, index) => pickRefAlias(index)).join(" ");
  const chipsLegacy = bundle.picks.map((ref) => `[${pickRefChipLabel(ref)}]`).join(" ");
  if (remainder === chipsOnly || remainder === chipsLegacy) remainder = "";
  return { bundle, remainder };
}

function pickLine(ref: PickRef, index: number): string {
  const text = ref.text.trim() || "（无可见文字）";
  const tag = oneLine(ref.tag) || "div";
  return `${pickRefAlias(index)} <${tag}> ${text} · ${ref.path}`;
}

/** 多选点选改页提示词：@n 索引 + 用户混排需求；全文由 read_page 取。 */
export function buildMultiPickEditPrompt(
  input: PickRefPageContext & { userRequest: string; picks: PickRef[] },
): string {
  const title = oneLine(input.pageTitle ?? "") || input.pageSlug || "当前页";
  const picks = input.picks;
  const primary = picks[0];
  const tagSummary =
    picks.length === 1
      ? oneLine(primary?.tag ?? "") || "div"
      : picks.map((ref) => oneLine(ref.tag) || "div").join(", ");

  return [
    PICK_EDIT_MARKER_START,
    envelopeField("slug", input.pageSlug),
    envelopeField("title", title),
    envelopeField("path", input.pagePath || "/"),
    envelopeField("tag", tagSummary),
    envelopeField("count", String(picks.length)),
    PICK_EDIT_MARKER_END,
    "",
    ...picks.map((ref, index) => pickLine(ref, index)),
    "",
    `read_page(${input.pageSlug}) → 按 @n 路径定位 → patches 预览`,
    "",
    `需求：${input.userRequest.trim()}`,
  ].join("\n");
}

export function buildPickEditPromptFromComposer(content: PickRefComposerContent): string {
  return buildMultiPickEditPrompt({
    pageSlug: content.pageSlug,
    pageTitle: content.pageTitle,
    pagePath: content.pagePath,
    picks: content.picks,
    userRequest: content.userRequest,
  });
}

export function buildPickEditPromptFromBundle(
  bundle: PickRefBundle,
  userRequest: string,
): string {
  return buildMultiPickEditPrompt({
    pageSlug: bundle.pageSlug,
    pageTitle: bundle.pageTitle,
    pagePath: bundle.pagePath,
    picks: bundle.picks,
    userRequest,
  });
}
